<template>
    <div class="compare__schools" :class="{ 'loading': loading }">
        <div v-if="loading && compareSchools.length < 1">
            <div class="loading__container"></div>
        </div>

        <div v-else-if="compareList.length === 0" class="my-dashboard__empty">
            <h3 class="step--4 black-primary margin--xs-s">
                {{ printString("your_list_is_empty") }}
            </h3>
            <p class="paragraph--teaser margin--s-m">
                {{ printString("add_schools_to_compare_list") }}
            </p>
            <button class="general-button orange-background"
                @click.prevent="goBackToSchools"
                @keydown.enter.prevent="goBackToSchools"
                
            >
                {{ printString("find_schools") }}
            </button>
        </div>

        <div v-else class="compare__schools__list compare__list" :class="{ 'loading': loading }">
            <div class="compare__schools__line compare__list__line">
                <p class="paragraph--teaser">
                    {{ printString('compare_up_to_3_schools') }}
                </p>

                <div v-for="school in dataSchoolGeneral" :key="school.id" class="compare__schools__item compare__item">
                    <a class="compare__item__link" :href="`${schoolUrl}${school.id}/`" 
                        @click.prevent="goToSchoolPage(school.id)"
                        @keydown.enter.prevent="goToSchoolPage(school.id)"
                    >
                        <figure class="margin--xs-s">
                            <img :src="school.logo.src" :alt="school.logo.title" width="100"/>
                        </figure>
                        <h2 class="step--2 margin--xs-s">{{ school.name }}</h2>
                    </a>
                    <a :href="school.url" class="secondary-button red-color" v-if="school.url" target="_blank">
                        {{ printString('official_website') }}
                        <span v-html="ico_external_16"/>
                    </a>
                    <button 
                        @click="handleRemoveFromCompare(school)"
                        class="general-button border-grey only-icon"
                    >
                        <span v-html="ico_close_16"/>
                        <span class="vh">{{ printString('remove') }}</span>
                    </button>
                </div>
            </div>

            <div class="compare__schools__line compare__list__line" v-if="dataLocation.length > 0">
                <p class="step--1 black-primary">
                    {{ printString('location') }}
                </p>
                <div class="compare__schools__item compare__item"
                     v-for="location in dataLocation"
                     :key="location.city"
                >
                    <p v-if="location.city">{{ location.city }}, {{ location.province }}</p>
                    <p v-else>{{ location.province }}</p>
                </div>
            </div>

            <div class="compare__schools__line compare__list__line" v-if="dataCampus.length > 0">
                <p class="step--1 black-primary">
                    {{ printString('campus_life') }}
                </p>
                <div class="compare__schools__item compare__item enrollment-holder"
                     v-for="campus in dataCampus"
                     :key="campus.full_time_undergraduate + campus.full_time_graduate + campus.part_time_undergraduate + campus.part_time_graduate"
                >
                    <ul>
                        <li class="enrollment-item" v-if="campus.full_time_undergraduate">
                            <p>{{ printString('full_time_undergraduate') }}:</p>
                            <p class="step--3">{{ formatNumber(campus.full_time_undergraduate) }}</p>
                        </li>
                        <li class="enrollment-item" v-if="campus.full_time_graduate">
                            <p>{{ printString('full_time_graduate') }}:</p>
                            <p class="step--3">{{ formatNumber(campus.full_time_graduate) }}</p>
                        </li>
                        <li class="enrollment-item" v-if="campus.part_time_undergraduate">
                            <p>{{ printString('part_time_undergraduate') }}:</p>
                            <p class="step--3">{{ formatNumber(campus.part_time_undergraduate) }}</p>
                        </li>
                        <li class="enrollment-item" v-if="campus.part_time_graduate">
                            <p>{{ printString('part_time_graduate') }}:</p>
                            <p class="step--3">{{ formatNumber(campus.part_time_graduate) }}</p>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="compare__schools__line compare__list__line" v-if="dataTuition.length > 0">
                <p class="step--1 black-primary">
                    {{ printString('tuition') }}
                </p>
                <div    class="compare__schools__item compare__item enrollment-holder"
                        v-for="tuition in dataTuition"
                >
                    <ul>
                        <li class="enrollment-item">
                            <p class="step--1 black-primary">
                                {{ printString('canadian_students') }}
                            </p>
                        </li>
                        <li class="enrollment-item" v-if="tuition.regular_undergraduate">
                            <p>{{ printString('undergraduate_tuition_fees') }}</p>
                            <p class="step--3"> ${{ formatNumber(tuition.regular_undergraduate) }}</p>
                        </li>
                        <li class="enrollment-item" v-if="tuition.regular_graduate">
                            <p>{{ printString('graduate_tuition_fees') }}</p>
                            <p class="step--3"> ${{ formatNumber(tuition.regular_graduate) }}</p>
                        </li>
                        <li class="enrollment-item" v-if="tuition.out_of_province_undergraduate">
                            <p>{{ printString('out_of_province_tuition_fees') }}</p>
                            <p class="step--3"> ${{ formatNumber(tuition.out_of_province_undergraduate) }}</p>
                        </li>
                        <li class="enrollment-item" v-if="tuition.out_of_province_graduate">
                            <p>{{ printString('out_of_province_tuition_fees') }}</p>
                            <p class="step--3"> ${{ formatNumber(tuition.out_of_province_graduate) }}</p>
                        </li>
                        <li class="enrollment-item black-primary" v-if="tuition.international_undergraduate || tuition.international_graduate">
                            <p class="step--1 black-primary">
                                {{ printString('international_students') }}
                            </p>
                        </li>
                        <li class="enrollment-item" v-if="tuition.international_undergraduate">
                            <p>{{ printString('undergraduate_tuition_fees') }}</p>
                            <p class="step--3"> ${{ formatNumber(tuition.international_undergraduate) }}</p>
                        </li>
                        <li class="enrollment-item" v-if="tuition.international_graduate">
                            <p>{{ printString('graduate_tuition_fees') }}</p>
                            <p class="step--3"> ${{ formatNumber(tuition.international_graduate) }}</p>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="compare__schools__line compare__list__line" v-if="dataApplicationFee.length > 0">
                <p class="step--1 black-primary">
                    {{ printString('application_fee') }}
                </p>
                <div class="compare__schools__item compare__item"
                     v-for="fee in dataApplicationFee"
                     :key="fee.application_fee"
                >
                    <p>{{ fee.application_fee }}</p>
                </div>
            </div>
        </div>

        <div class="loader" v-if="compareSchools.length > 0"></div>
    </div>
</template>

<script>
import { useRoute, useRouter } from 'vue-router';

export default {
    name: 'CompareSchools',
    data() {
        return {
            compareSchools: [],
            compareList: [],
            loading: true,
            ico_close_16: window.ico_close_16,
            ico_external_16: window.ico_external_16,
            activeRequests: 0,
            schoolUrl: window.school_url,
        };
    },
    created() {
        this.route = useRoute();
        this.router = useRouter();
        this.loadCompareListFromStorage();
    },
    computed: {
        dataSchoolGeneral() {
            return this.compareSchools.map(school => ({
                logo: school.university[0].university_logo,
                name: school.university[0].name,
                url: school.university[0].url,
                id: school.university[0].university_id,
                alt_lang_id: school.university[0].alt_lang_id
            }));
        },
        dataLocation() {
            const locations = this.compareSchools.map(school => ({
                city: school.university[0].city,
                province: school.university[0].province
            }));
            return locations.every(loc => loc.city === "" && loc.province === "") ? [] : locations;
        },
        dataCampus() {
            return this.compareSchools.map(school => ({
                full_time_undergraduate: school.university[0].enrollment.full_time_undergraduate,
                full_time_graduate: school.university[0].enrollment.full_time_graduate,
                part_time_undergraduate: school.university[0].enrollment.part_time_undergraduate,
                part_time_graduate: school.university[0].enrollment.part_time_graduate,
            })).filter(enrollment => 
                enrollment.full_time_undergraduate || 
                enrollment.full_time_graduate || 
                enrollment.part_time_undergraduate || 
                enrollment.part_time_graduate
            );
        },
        dataTuition() {
            return this.compareSchools.map(school => ({
                regular_undergraduate: school.university[0].tuition_fees.regular_undergraduate,
                regular_graduate: school.university[0].tuition_fees.regular_graduate,
                out_of_province_undergraduate: school.university[0].tuition_fees.out_of_province_undergraduate,
                out_of_province_graduate: school.university[0].tuition_fees.out_of_province_graduate,
                international_undergraduate: school.university[0].tuition_fees.international_undergraduate,
                international_graduate: school.university[0].tuition_fees.international_graduate,
            })).filter(tuition => 
                tuition.regular_undergraduate || 
                tuition.regular_graduate || 
                tuition.out_of_province_undergraduate || 
                tuition.out_of_province_graduate || 
                tuition.international_undergraduate || 
                tuition.international_graduate
            );
        },
        dataApplicationFee() {
            const fees = this.compareSchools.map(school => ({
                application_fee: school.university[0].application_fee
            }));
            return fees.every(fee => (fee.application_fee === null || fee.application_fee === '')) ? [] : fees;
        }
    },
    methods: {
        startLoading() {
            this.activeRequests++;
            this.loading = true;
            console.log('startLoading active requests', this.activeRequests);
        },
        finishLoading() {
            this.activeRequests--;
            if (this.activeRequests === 0) {
                this.loading = false;
            }
            console.log('finishLoading active requests', this.activeRequests);
        },
        goToSchoolPage(schoolId) {
            // const routeName = window.lang === 'fr' ? 'schoolSingleFr' : 'schoolSingle';
            const routeName = window.lang === 'fr' ? 'schoolSingle' : 'schoolSingle';
            this.$router.push({ name: routeName, params: { id: schoolId } });
        },
        goBackToSchools() {
            // const route = this.site_lang === 'fr' ? '/fr/schools' : '/schools';
            const route = this.site_lang === 'fr' ? '/schools' : '/schools';
            this.router.push({ path: route });
        },
        loadCompareListFromStorage() {
            const storedList = localStorage.getItem('compareList');
            if (storedList) {
                this.compareList = JSON.parse(storedList);
                if (this.compareList.length > 0) {
                    this.fetchCompareSchools(this.compareList);
                } else {
                    this.compareSchools = [];
                    this.loading = false;
                }
            } else {
                this.compareList = [];
                this.loading = false;
            }
        },
        saveCompareListToStorage() {
            localStorage.setItem('compareList', JSON.stringify(this.compareList));
        },
        async fetchCompareSchools(compareList) {
            this.startLoading();
            try {
                const relevantIds = compareList.map(pair => {
                    const index = window.lang === 'en' ? 0 : 1;
                    return pair[index];
                }).filter(id => id !== '');

                const requests = relevantIds.map(id => 
                    fetch(`${window.api_base_url}/api/universities-single/`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            apikey: window.api_key,
                            university_id: id,
                            site_lang: window.lang,
                        }),
                    }).then(response => response.json())
                );

                const results = await Promise.all(requests);
                this.compareSchools = results;

            } catch (error) {
                console.error("Error fetching compare schools:", error);
            } finally {
                this.finishLoading();
            }
        },
        async handleRemoveFromCompare(school) {
            this.startLoading();
            try {
                console.log('handleRemoveFromCompare', school);
                const schoolPair = [
                    this.site_lang === 'en' ? school.id : school.alt_lang_id,
                    this.site_lang === 'fr' ? school.id : school.alt_lang_id
                ];
                
                const index = this.compareList.findIndex(pair => 
                    pair[0] === schoolPair[0] || pair[1] === schoolPair[1]
                );
                
                if (index !== -1) {
                    this.compareList.splice(index, 1);
                }

                this.saveCompareListToStorage();
                if (this.compareList.length < 1) {
                    this.compareSchools = [];
                } else {
                    await this.fetchCompareSchools(this.compareList);
                }

            } catch (error) {
                console.error("Error in compare operation:", error);
            } finally {
                this.finishLoading();
            }
        },
        printString(string) {
            return window.translations[string];
        },
        formatNumber(number) {
            return number ? number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
        }
    },
};
</script>

